import {
  REPORT_WIZARD_STEPS,
  ReportsWizardProvider,
  useReportsWizard,
} from "./ReportsWizardProvider";
import Button, { ButtonTypes } from "../shared-components/Button";
import { ButtonColorTypes } from "../shared-components/Button";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import PageHeader from "../shared-components/PageHeader";
import Container from "../shared-components/Container";
import { ReportsPendingReviewItem } from "../api/Reports";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { BasicFields, ReportsWizardBasic } from "./ReportsWizardBasic";
import { ReportsWizardSidebar } from "./ReportsWizardSidebar";
import { ReportsWizardPersonInfo } from "./ReportsWizardPersonInfo";
import { ReportsWizardChecktype } from "./ReportsWizardChecktype";
import { ReportsWizardAdditionalInfo } from "./ReportsWizardAdditionalInfo";
import { ReportsWizardDocumentUpload } from "./ReportsWizardDocumentUpload";
import { ReportsWizardSummary } from "./ReportsWizardSummary";
import { useEffect } from "react";
import LoadingSpinner from "../shared-components/LoadingSpinner";

type Props = {};

const ReportsWizard = (props: Props) => {
  const { setBasicInfo, currentStep, initialize, isLoadingRerunInfo } =
    useReportsWizard();

  const location = useLocation();
  const locationState = location.state as {
    rerun: boolean;
    parent_report: ReportsPendingReviewItem;
  };
  const navigate = useNavigate();

  const { canBulkUploadReports } = usePermissionValidator();

  useEffect(() => {
    // initial reset of values
    if (locationState?.rerun) {
      initialize(true, locationState.parent_report);
    } else {
      initialize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateBack = () => {
    navigate("/reports");
  };

  const handleBasicFieldsChange = (fields: BasicFields) => {
    setBasicInfo({
      firstName: fields.firstName,
      middleName: fields.middleName,
      lastName: fields.lastName,
      companyUUID: fields.companyUUID,
      jobTitle: fields.jobTitle,
      clientReportId: fields.clientReportID,
    });
  };

  return (
    <div className="-mx-6">
      <div
        className="px-24 max-w-[130rem] mx-auto"
        data-testid="reports-wizard-container"
      >
        <div
          className="flex gap-3 text-xs font-thin text-gray-700 mb-4 items-center cursor-pointer"
          onClick={navigateBack}
        >
          <ChevronLeftIcon className="w-3" />
          <span>Back to Reports</span>
        </div>
        {isLoadingRerunInfo && (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-xl py-8">
              Loading parent report information
            </div>
            <LoadingSpinner />
          </div>
        )}
        {currentStep !== REPORT_WIZARD_STEPS.SUMMARY && !isLoadingRerunInfo && (
          <>
            <PageHeader
              title={
                locationState?.parent_report ? "Rerun Report" : "Add Report"
              }
            >
              <div className="flex gap-4 items-center">
                {canBulkUploadReports && (
                  <Button
                    type={ButtonTypes.default}
                    text={"Bulk Upload Reports"}
                    colorType={ButtonColorTypes.alternate}
                    onClick={() => navigate("/reports/bulkupload")}
                  />
                )}
              </div>
            </PageHeader>

            <Container className="mb-8">
              <ReportsWizardBasic onChange={handleBasicFieldsChange} />
            </Container>
            <Container>
              <div className="flex">
                <div className="w-52 pr-2 overflow-hidden">
                  <ReportsWizardSidebar />
                </div>

                <div className="flex-1">
                  {currentStep === REPORT_WIZARD_STEPS.PERSON_INFO && (
                    <ReportsWizardPersonInfo />
                  )}
                  {currentStep === REPORT_WIZARD_STEPS.CHECKTYPES && (
                    <ReportsWizardChecktype />
                  )}
                  {currentStep === REPORT_WIZARD_STEPS.ADDITIONAL_INFO && (
                    <ReportsWizardAdditionalInfo />
                  )}
                  {currentStep === REPORT_WIZARD_STEPS.DOCUMENT_UPLOAD && (
                    <ReportsWizardDocumentUpload />
                  )}
                </div>
              </div>
            </Container>
          </>
        )}
        {currentStep === REPORT_WIZARD_STEPS.SUMMARY && (
          <>
            <PageHeader title={"Summary"}></PageHeader>
            <ReportsWizardSummary />
          </>
        )}
      </div>
    </div>
  );
};

const ReportsWizardWrapper = () => {
  return (
    <ReportsWizardProvider>
      <ReportsWizard />
    </ReportsWizardProvider>
  );
};

export default ReportsWizardWrapper;
